
import VGRoute from './VGRoute';
import { VGNavigation } from './VGNavigation';
import VGNavigationHelper from './VGNavigationHelper';
import {
  VIEWPOINT_PADDING,
  UnifiedPOI,
  collectServices,
  toViewpoint,
  getClosestService,
  getClosest,
  getLocalized,
  getOptions,
  getPlacesByCategories,
  getPOI,
  getViewpoint,
  hideIMDs,
  splitCode,
  splitAndDecode,
  validateMapData
} from './vg-utils';

import Events from '~/events';
import Deferred from '~/deferred';

export default function VGMiddleware (options, targetElement) {

  const defaults = {
    cameraType: 'perspective',
    enableStats: false,
    initialFloor: undefined,
    initialPoi: undefined,
    initialLang: 'fi',
    initialAnimation: 0,
    useMouseover: false,
    showSidebar: false,
    activePlaceColor: '#8E7434',
    pathColor: '#8E7434',
    appParams: {},
    mapURL: null,
    minRadius: 100,
    onObjectMouseUp: () => console.warn('Not implemented "onObjectMouseUp"'),
    animationType: 'translation',
    changeFloorAnimationDuration: 500,
    accessibleRoute: false,
    viewType: 'singlefloor',
    logoPosition: 'BOTTOM_RIGHT',
    floorMapping: {}
  };

  const validCategories = [
    'imd', 'label.relaxedSize',
    ...Object.keys(options.serviceMapping)
  ];

  const vgContext = {
    options: Object.assign({}, defaults, options),
    state: {
      accessibleRoute: !!options.accessibleRoute,
      lastRouteRequest: null
    },
    selectorPOIs: [],
    targetElement,
    vgMiddleware: this,
    updateProgress: p => p == 1 && console.log('Visioglobe SDK is ready')
  };

  vgContext.onObjectMouseUp = (event, element) => {
    vgContext.options.onObjectMouseUp(event, element); // Pass to controller
    vgContext.vgMiddleware.setActivePlace(element);
  };

  // Curry functions with context and add into middleware
  [ [setActivePlace, 'setActivePlace'],
    [resetActivePlace, 'resetActivePlace'],
    [setupInitialPosition, 'setupInitialPosition'],
    [setHighlightedPlace, 'setHighlightedPlace'],
    [resetHighlightedPlace, 'resetHighlightedPlace'],
    [selectPlace, 'selectPlace'],
    [selectPOI, 'selectPOI'],
    [gotoFloorAndPosition, 'gotoFloorAndPosition'],
    [mapFloor, 'mapFloor'],
    [changeFloor, 'changeFloor'],
    [showRouteTo, 'showRouteTo'],
    [reroute, 'reroute'],
    [doRouting, 'doRouting'],
    [clearRouting, 'clearRouting'],
    [goToInstruction, 'goToInstruction'],
    [showClosestService, 'showClosestService'],
    [unPopServices, 'unPopServices'],
    [popService, 'popService'],
    [popSingleService, 'popSingleService'],
    [unload, 'unload'],
    [zoom, 'zoom'],
    [pan, 'pan'],
    [start, 'start'],
    [stop, 'stop'],
    [resize, 'resize'],
    [highlight, 'highlight'],
    [get, 'get'],
    [getState, 'getState'],
    [addSelectorPOI, 'addSelectorPOI']
  ].forEach(([fn, name]) => vgContext.vgMiddleware[name] = fn(vgContext));

  if (!isWebGLSupported()) {
    throw new Error('WebGL not supported');
  }

  options.devMode && (window.VGMiddleware = this);

  // Setup deferred Promises
  [
    'onceReady',
    'onceServicesReady'
  ].forEach(Deferred.addLock(this));

  vgContext.pMapviewer = init(vgContext)
    .then(mapviewer => {
      vgContext.mapviewer = mapviewer;
      validateMapData(mapviewer, validCategories);
      onLoadCompleted(vgContext);
      console.log('VGMiddleware ready');
      this.deferred.onceReady.resolve(this);
      return mapviewer;
    })
    .catch(e => console.error(e));
}

// init :: VGContext -> Promise Mapviewer
const init = vgContext => {

  const { vg } =  window;

  if (typeof vg === 'undefined') {
    throw new Error('Missing VisioWeb SDK...probably missing script file vg.mapviewer.web.js or not loaded yet');
  }

  const {
    mapURL,
    cameraType,
    initialFloor,
    logoPosition,
    devMode
  } = vgContext.options;

  const params = {
    logoPosition,
    path: mapURL,
    cameraType,
    antialias: true,
    initialFloorName: initialFloor,
    onObjectMouseUp: vgContext.onObjectMouseUp,
    progressHandler: vgContext.updateProgress
  };

  const mapviewer = new vg.mapviewer.Mapviewer();
  devMode && (window.mapviewer = mapviewer);

  return mapviewer
    .initialize(vgContext.targetElement, params)
    .then(_ => mapviewer);
};


// onLoadCompleted :: VGContext -> void
const onLoadCompleted = vgContext => {

  const { mapviewer, vgMiddleware } = vgContext;
  const { enableStats, statURL, imdHash } = vgContext.options;

  if (enableStats) {
    mapviewer.setLogStatistic(true);
    if (statURL) {
      mapviewer.setLogStatisticURL(statURL);
    }
    mapviewer.camera.track = true;
  }

  // SETUP camera
  [
    'maxRadius', 'minRadius', 'maxPitch', 'minPitch',
    'heading', 'pitch', 'rotation'
  ].forEach(x => {
    const value = vgContext.options[x];
    if (Number.isFinite(value)) {
      mapviewer.camera[x] = value;
    }
  });

  const limits = mapviewer.getFootprintPoints('limits');
  if (limits) {
    mapviewer.camera.setBoundary(limits);
  }

  vgContext.placesOnMap = loadPlaceNames(vgContext);

  // Collect services
  const { servicePOIs, serviceTypes, serviceMap } = collectServices(vgContext);
  Object.assign(vgContext, { servicePOIs,serviceTypes, serviceMap });
  vgMiddleware.deferred.onceServicesReady.resolve({ servicePOIs,serviceTypes, serviceMap });

  const [imdPOI] = getPOI(mapviewer)(imdHash);
  if (imdPOI.isError) {
    return console.error(`Missing IMD '${imdHash}' in VisioGlobe map data`);
  }
  vgContext.imd = Object.assign({ poi: imdPOI }, imdPOI.options());

  addIMD(vgContext);
  handleSelectorPOIs(vgContext);
};

const unload = vgContext => () => vgContext.mapviewer.unloadMap();

const getState = vgContext => () => vgContext.state;
const get = vgContext => () => vgContext;

const goToInstruction = vgContext => index => {
  const { currentNavigation, navigationHelper } = vgContext.state;
  return currentNavigation.setCurrentIndex(index)
    .then(_ => navigationHelper);
};


// start :: VGContext -> () -> void
const start = vgContext => () => vgContext.mapviewer.start();
// stop :: VGContext -> () -> void
const stop = vgContext => () => vgContext.mapviewer.stop();

//#region: View setup -----------------------------------------

const getDefaultFloor = extraData => {
  const { defaultBuilding, buildings } = extraData.config.venue_layout;
  return buildings[defaultBuilding].defaultFloor;
};

/*
 * Customizes how the map looks at the beginning.
 */
const setupInitialPosition = vgContext => (toFloor) => {

  const { mapviewer, imd = {}, vgMiddleware } = vgContext;
  const {
    initialFloor = toFloor || imd.floor || getDefaultFloor(mapviewer.getExtraData()),
    initialPoi = imd.id,
    initialRadius
  } = vgContext.options;

  let floor, viewpoint;

  try {
    const poi = UnifiedPOI.from(initialPoi, mapviewer);
    floor = poi.options.floor;
    viewpoint = poi.getViewpoint(mapviewer, initialRadius);
  } catch (e) {
    console.warn(`Bad poi '${initialPoi}'`);
    floor = initialFloor;
    viewpoint = getViewpoint(initialFloor, mapviewer);
  }


  return vgMiddleware.gotoFloorAndPosition(floor, viewpoint, mapviewer)
    .catch(e => {
      console.error(`Failed goto floor '${floor}' and vp ${viewpoint}`, e);
      return mapviewer.changeFloor(initialFloor, { animationDuration: 500 });
    });
};

/*
 * Go to that position. change floor if needed
 */
const gotoFloorAndPosition = vgContext => (floor, position, postFunction = () => {}) => {

  const { mapviewer } = vgContext;
  const { changeFloorAnimationDuration } = vgContext.options;

  const gotoFunction = function() {
    return mapviewer.camera
      .goTo(position, { animationDuration: 500 })
      .then(postFunction);
  };

  if (mapviewer.getCurrentFloor() == floor) {
    return gotoFunction();
  } else {
    return mapviewer
      .changeFloor(floor, { animationDuration: changeFloorAnimationDuration })
      .then(gotoFunction);
  }
};

const mapFloor = vgContext => id => vgContext.options.floorMapping[id] || {};

const changeFloor = vgContext => (id) => {
  const { mapviewer } = vgContext;
  const { floorMapping } = vgContext.options;
  const { vgID } = floorMapping[id];
  mapviewer.changeFloor(vgID, { animationDuration: 500 })
    .catch(e => console.log(`Floor change to '${id}' failed`, e));
};

const zoom = vgContext => (scale = 1) => {
  const { mapviewer } = vgContext;
  const { x, y, radius } = mapviewer.camera.position;
  mapviewer.camera.goTo({ x, y, radius: radius * scale });
};

const vector = ({ x, y }, angle, length) => {
  const rad = angle * Math.PI / 180;
  return {
    x: length * Math.cos(rad) + x,
    y: length * Math.sin(rad) + y
  };
};

const pan = vgContext => ({ h = 0, v = 0 }) => {
  const { mapviewer } = vgContext;
  const { position, rotation } = mapviewer.camera;
  const mod = v ? 90 : 0; // vertical axis is obviously + 90 degrees
  mapviewer.camera.goTo(vector(position, rotation + mod, h || v));
};

const resize = vgContext => (width, height) => {
  const { mapviewer } = vgContext;
  mapviewer.resize(width, height);
};

const highlight = vgContext => () => {
  vgContext.pMapviewer.then(mapviewer => {
    const pois = mapviewer.getPOIs();
    console.log(pois);
  });
};

const addIMD = vgContext => {
  const { mapviewer, vgMiddleware, imd, options } = vgContext;

  hideIMDs(mapviewer);
  const scale = popScale(options);

  // Just highlight IMD were on running
  vgMiddleware.addSelectorPOI({
    floor: imd.floor,
    selector: '#you-are-here',
    position: imd.position
  }, scale);
};

// popScale :: (Number, Number) -> Number
const popScale = ({ iconSize = 24 }, poiSize = 48) => iconSize / poiSize + .05;

// popService :: VGContext -> Number -> void
const popService = vgContext => serviceId => {
  const { vgMiddleware, mapviewer, serviceMap, options } = vgContext;
  const service = serviceMap[serviceId];
  const scale = popScale(options);
  const currentFloor = mapviewer.getCurrentFloor();

  vgMiddleware.unPopServices();

  Events.dispatchEvent('service:highlight', { name: service.name });
  Events.dispatchEvent('poi', service.name );

  // Collect service positions in current floor
  const positions = service.pois.reduce((acc, id) => {
    const poi = UnifiedPOI.from(id, mapviewer);
    if (poi.options.floor == currentFloor) {
      _popSingleService(id, scale, vgContext);
      acc.points.push(poi.getViewpoint(mapviewer));
    }
    return acc;
  }, Object.assign(
    { points: [] },
    VIEWPOINT_PADDING
  ));

  const viewpoint = mapviewer.getViewpointFromPositions(positions);
  vgMiddleware.gotoFloorAndPosition(currentFloor, viewpoint, mapviewer);
  return service;
};

const _popSingleService = (id, scaleFactor, vgContext) => {

  const { vgMiddleware, mapviewer } = vgContext;

  const options = getOptions(id, mapviewer);
  const position = Object.assign({}, options.position);

  vgMiddleware.addSelectorPOI({
    floor: options.floor,
    // Ids may begin with a digit ('1-atm-01'), which '#id' doesn't allow
    selector: `[id="${id}"]`,
    position: position
  }, scaleFactor);
};

// popSingleService :: VGContext -> String -> void
const popSingleService = vgContext => id => {
  const { vgMiddleware, options } = vgContext;
  const scale = popScale(options);
  vgMiddleware.unPopServices();
  _popSingleService(id, scale, vgContext);
};

// unPopServices :: VGContext -> () -> void
const unPopServices = vgContext => () => {
  const { selectorPOIs } = vgContext;

  const rest = selectorPOIs.splice(1);
  rest.forEach(({ poi }) => poi.remove());
};

// addSelectorPOI :: VGContext -> (POIOptions, Number) -> void
const addSelectorPOI = vgContext => (options, scaleFactor = 1) => {
  const { mapviewer, selectorPOIs } = vgContext;
  const { selector } = options;
  const el = document.querySelector(selector);

  try {
    const poi = mapviewer.addPOI(options);
    selectorPOIs.push({ el, scaleFactor, poi });
  } catch (e) {
    console.error(e);
  }
};

// cot :: Number -> Number
const cot = deg => 1 / Math.tan(deg * Math.PI/180);

// transformValues :: CSSStyleDeclaration -> { scale, x, y }
const transformValues = (transform = '') => {
  try {
    const { m11: scale, m41: x, m42: y } = new DOMMatrix(transform);
    return { scale, x, y };
  } catch (e) {
    console.log('Cant read ', transform);
    return { scale: 1, x: 0, y: 0 };
  }
};

const handleSelectorPOIs = vgContext => {

  const { mapviewer, selectorPOIs, targetElement } = vgContext;

  const applyFactor = (scale, f) => {
    if (scale == 1)     return 1;
    else if (scale < 0) return 1 - (1 - scale) * f;
    else                return (scale - 1) * f + 1;
  };

  // Need to take camera pitch into account. POIs on bottom of view need to
  // appear bigger than top.
  const { offsetHeight: maxY } = targetElement;
  const yScale = cot(-mapviewer.camera.pitch);
  const pitchEffect = y => yScale * (y / maxY) + 1;

  const origRadius = mapviewer.camera.position.radius;
  let lastRadius = origRadius;
  let scale = 1;

  mapviewer.on('redraw', _ => {
    const { radius } = mapviewer.camera.position;
    if (radius != lastRadius) {
      lastRadius = radius;
      scale = origRadius / radius;
    }

    selectorPOIs.forEach(({el, scaleFactor}) => {
      const style = window.getComputedStyle(el);
      const { y } = transformValues(style.transform);
      const poiScale = applyFactor(scale, scaleFactor) * pitchEffect(y);
      const translate = (el.style.transform || '').replace(/\s*scale\([\d.]+\)/g, '');
      el.style.transform = `${translate} scale(${poiScale})`;
    });
  });

  mapviewer.on('floorWillChange',
    () => selectorPOIs.forEach(({ poi }) => poi.hide())
  );

  mapviewer.on('floorChanged',
    () => selectorPOIs.forEach(({ poi }) => poi.show())
  );

};

// //#endregion
//#region: Place data -----------------------------------------

// loadPlaceNames :: VGContext -> Object
const loadPlaceNames = vgContext => {

  const { mapviewer } = vgContext;
  const { initialLang, stores } = vgContext.options;
  // Place and floor names in map bundle
  const { places, venueLayout = {} } = getLocalized(mapviewer.getExtraData(), initialLang);

  // Add floors
  const placesOnMap = Object.entries(venueLayout).reduce((acc, [k,v]) =>
    (acc[k] = { name: v.shortName || v.name }, acc), {}
  );

  const values = (obj) => Object.keys(obj).reduce((acc, key) => {
    const keys = key.split(/,\s*/);
    const { name } = obj[key];
    const values = keys.map(k => ({ id: k, name }));
    return (values[0].id == 'undefined' ? acc : acc.concat(values));
  }, []);

  const combined = values(places).concat(values(stores));
  const relaxedSizeLabels = getPlacesByCategories(['label.relaxedSize'], mapviewer);

  combined.forEach(({ id, name }) => {
    placesOnMap[id] = { name };
    const isRelaxedSize = !!relaxedSizeLabels[id];
    mapviewer.setPlaceName(id, {
      text: name,
      visibilityRampStartInvisible: 800,
      visibilityRampFullyInvisible: 1000,
      textTextureHeight: 128,
      zoomScaleFactor: isRelaxedSize ? 0 : 1.5,
      multiline: true
    });
  });

  return placesOnMap;
};

//#region: Place activation / highlight -----------------------------------------

// setActivePlace :: VGContext -> VGPLace -> void
const setActivePlace = vgContext => place => {
  if (place === false) {
    return;
  }

  const { mapviewer, state, vgMiddleware } = vgContext;
  const { activePlaceColor } = vgContext.options;

  // If the place is actually a POI, find the place with the same id.
  if (place.vg && place.vg.poi) {
    place = mapviewer.getPlace(
      place.options ? place.options('id') : place.vg.id
    );
    if (!place) {
      return;
    }
  }

  if (place == state.activePlace) {
    return;
  }

  vgMiddleware.resetActivePlace();

  if (place && !place.vg.poi) {
    mapviewer.setPlaceColor(place, activePlaceColor);
    state.activePlace = place;

    // a place cannot be highlighted and active at the same time.
    if (state.highlightedPlace == state.activePlace) {
      state.highlightedPlace = null;
    }
  }
};

// resetActivePlace :: VGContext -> _ -> void
const resetActivePlace = vgContext => () => {
  const { mapviewer, state } = vgContext;
  if (state.activePlace && !state.activePlace.vg.poi) {
    mapviewer.resetPlaceColor(state.activePlace);
    state.activePlace = null;
  }
};

const setHighlightedPlace = vgContext => place => {

  const { mapviewer, state, vgMiddleware } = vgContext;
  const alreadyHighlighted = place == state.highlightedPlace;
  const notAcceptablePLace =
    !place ||
    place instanceof Array  ||
    (place.vg && place.vg.poi);

  if (notAcceptablePLace || alreadyHighlighted) {
    return;
  }

  vgMiddleware.resetHighlightedPlace();
  if (typeof place != 'undefined' && place != state.activePlace) {
    mapviewer.setPlaceColor(place, 0xffffff - place.vg.originalColor);
    state.highlightedPlace = place;
  }
};

const resetHighlightedPlace = vgContext => () => {
  const { mapviewer, state } = vgContext;
  if (state.highlightedPlace !== null) {
    mapviewer.resetPlaceColor(state.highlightedPlace);
    state.highlightedPlace = null;
  }
};

// selectPlace :: VGContext -> String -> Promise
const selectPlace = vgContext => placeName => {
  const { mapviewer, vgMiddleware } = vgContext;
  const codes = splitAndDecode(placeName).map(x => x.spaceCode);
  return getClosest(codes, vgContext)
    .then(closest => {
      var place = mapviewer.getPlace(closest);
      vgMiddleware.resetActivePlace();
      if (place !== false) {
        // const poi = UnifiedPOI.from(closest, mapviewer);
        // const floor = poi.options.floor;
        // const viewpoint = poi.getViewpoint(mapviewer, options.minRadius);
        return vgMiddleware.gotoFloorAndPosition(place.vg.floor, place, function() {
          vgMiddleware.setActivePlace(place);
        });
      }
      return Promise.resolve();
    });
};

// selectPOI :: VGContext -> String -> Promise
const selectPOI = vgContext => id => {
  const { mapviewer, vgMiddleware, options } = vgContext;

  const poi = UnifiedPOI.from(id, mapviewer);
  const floor = poi.options.floor;
  const viewpoint = poi.getViewpoint(mapviewer, options.minRadius);

  vgMiddleware.unPopServices();
  return vgMiddleware.gotoFloorAndPosition(floor, viewpoint, () => {
    vgMiddleware.popSingleService(id);
  });
};

// //#endregion
// //#region: Route / navigation management -----------------------------------------

// showClosestService :: VGContext -> (Number, Options?) -> Promise Dest
//    Dest = { floor: String, position = {x, y, z, scale}, id: String }
const showClosestService = vgContext => (serviceType, {route = false, lang} = {}) => {
  const { vgMiddleware, serviceMap } = vgContext;

  return getClosestService(serviceType, vgContext)
    .then(({ floor, position, id }) => {
      let onceFinished;
      const service = serviceMap[serviceType];

      if (route) {
        onceFinished = vgMiddleware.showRouteTo(id, lang || vgContext.options.initialLang);
        Events.dispatchEvent('service:route', { name: service.name });
      }
      else {
        const viewpoint = toViewpoint(position, 150);
        onceFinished = vgMiddleware.gotoFloorAndPosition(floor, viewpoint);
        Events.dispatchEvent('service:closest:show', { name: service.name });
      }
      return onceFinished.then(() => ({ floor, position, id }));
    });
};

// showRouteTo :: VGContext -> (String, String) -> Promise VGNavigationHelper
const showRouteTo = vgContext => (destId, lang) => {
  const { vgMiddleware, state } = vgContext;
  const { imdHash, initialLang } = vgContext.options;
  const routeLang = lang || initialLang;
  const codes = splitAndDecode(destId).map(x => x.spaceCode);
  const place = vgContext.placesOnMap[destId];

  state.lastRouteRequest = {
    destId,
    lang: routeLang
  };

  // Service POIs may have no place in the map data
  Events.dispatchEvent('poi', { name: place ? place.name : destId, action: 'route' });
  return getClosest(codes, vgContext)
    .then(closest =>
      vgMiddleware.doRouting(imdHash, closest, routeLang)
    );
};

// reroute :: VGContext -> (?String) -> Promise VGNavigationHelper | null
const reroute = vgContext => (lang) => {
  const { state, vgMiddleware } = vgContext;
  const { lastRouteRequest } = state;

  if (!lastRouteRequest || !lastRouteRequest.destId) {
    return Promise.resolve(null);
  }

  vgMiddleware.clearRouting();
  const routeLang = lang || lastRouteRequest.lang;

  return vgMiddleware.showRouteTo(lastRouteRequest.destId, routeLang);
};

// doRouting :: VGContext -> (String, String, String) -> Promise VGNavigationHelper
const doRouting = vgContext => (srcPlace, dstPlace, language) => {

  const { mapviewer, vgMiddleware, state } = vgContext;
  const { accessibleRoute } = state;
  const { appParams = {}, pathColor } = vgContext.options;

  const defaults = {
    navigationParameters: {
      // firstNodeAsIntersection: true,
      modalityParameters: {
        shuttle: {
          straightAngleThreshold: 180.0,
          distanceFromCouloirThreshold: 1000.0
        }
      }
    }
  };

  const routingSettings = ((include = false, { routingParameters = {} }) => {
    const mergedRoutingParameters = Object.assign({}, routingParameters);

    if (include) {
      const excludedAttributes = new Set(
        Array.isArray(mergedRoutingParameters.excludedAttributes)
          ? mergedRoutingParameters.excludedAttributes
          : []
      );
      excludedAttributes.add('escalator');
      excludedAttributes.add('stairway');
      mergedRoutingParameters.excludedAttributes = Array.from(excludedAttributes);
    }

    return { routingParameters: mergedRoutingParameters };
  })(accessibleRoute, appParams);

  const settings = {
    language,
    src: srcPlace,
    // dst: dstPlace,
    computeNavigation: true
  };

  // If you did not want to display anything special with a modalityParameters
  // lRouteRequest.routingParameters.remapResultingModality: {'Travelator': 'pedestrian'}

  // Uncomment below if you don't want to use the defaults.
  // lRouteRequest.navigationParameters.algorithm = 'auto';
  // lRouteRequest.navigationParameters.algorithm = 'intersectionAlgorithm';
  // set firstNodeAsIntersection to be true if you want a turn right/left instruction when exiting a POI

  // if (typeof query.firstnode !== 'undefined') {
  //   lRouteRequest.navigationParameters.firstNodeAsIntersection = true;
  // }

  return getClosest(splitCode(dstPlace), vgContext)
    .then(closestDst => {
      const lRouteRequest = Object.assign(
        defaults,
        routingSettings,
        settings,
        { dst: closestDst }
      );

      return mapviewer.computeRoute(lRouteRequest);
    })
    .then(function(route) {

      if (route.data.status != 200) {
        vgMiddleware.clearRouting();
        return Promise.reject('Sorry, no route available between ' + route.request.src + ' and ' + route.request.dst + '.');
      }

      route.data.pathColor = pathColor;
      const currentRoute = state.currentRoute = new VGRoute(vgContext, route.data);
      if (currentRoute.isValid()) {
        currentRoute.show();
        state.currentNavigation = new VGNavigation(vgContext, route.data);
        state.navigationHelper = new VGNavigationHelper(vgContext, route.data.navigation);

        vgMiddleware.setActivePlace(mapviewer.getPlace(srcPlace));
        const viewpoint = currentRoute.getInitialViewpointPosition();
        const floorname = currentRoute.getInitialFloor();

        return vgMiddleware.gotoFloorAndPosition(floorname, viewpoint)
          .then(() => state.navigationHelper);
      } else {
        return Promise.reject('Problems rendering the route between '+ route.request.src + ' and ' + route.request.dst + '.');
      }
    })
    .catch(function(e) {
      vgMiddleware.clearRouting();
      console.error(e);
    });
};

/*
 * removes currentRoute
 * removes currentNavigation
 * resets start/end route points.
 */
const clearRouting = vgContext => () => {

  // const { mapviewer, vgMiddleware } = vgContext;
  const { currentRoute, currentNavigation } = vgContext.state;

  vgContext.state.navigationHelper = null;

  if (currentRoute || currentNavigation) {

    currentRoute && currentRoute.remove();
    currentNavigation && currentNavigation.remove();
    vgContext.state.currentRoute = null;
    vgContext.state.currentNavigation = null;
  }
};

//#endregion
//#region: Pointer events -----------------------------------------


function isWebGLSupported() {
  var result = false;
  if (window.WebGLRenderingContext) {
    var cvsEl;
    var ctx;
    cvsEl = document.createElement('canvas');
    ctx = cvsEl.getContext('webgl') || cvsEl.getContext('experimental-webgl');

    if (ctx) {
      result = true;
      // This makes sure the Browser supports WebGL and that it can create the WebGL context
    }
  }
  return result;
}
